/* eslint-disable @atlaskit/ui-styling-standard/no-important-styles */
import React, { useEffect, useState } from 'react';

import { cssMap as nativeCssMap } from '@compiled/react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Badge from '@atlaskit/badge';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Flex, Inline } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';
import Tabs, { TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';

import { getCurrentBuildIndex } from '../../utils';
import { useSolutionPlan } from '../solution-plan-context';

import { BuildTab } from './build-tab';
import { messages } from './messages';
import { useOverflowTabs } from './use-overflow-tabs';

const ANIMATION_DURATION = '350ms'; // no token exists for this yet, it seems. { durations.medium } from '@atlaskit/motion' === 350ms

const styles = cssMap({
	cardContent: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		maxHeight: '220px',
		overflow: 'clip',
		position: 'relative',
		transition: 'max-height',
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: ANIMATION_DURATION,
		// @ts-expect-error - cssMap type union does not include !important token strings; required for override specificity
		borderTopWidth: `${token('border.width')} !important`,
		// @ts-expect-error - cssMap type union does not include !important token strings; required for override specificity
		borderTopStyle: 'solid !important',
		// @ts-expect-error - cssMap type union does not include !important token strings; required for override specificity
		borderTopColor: `${token('color.border')} !important`,
	},
	cardContent__gradientSkrim: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		right: 0,
		height: '60px',
		transition: 'opacity',
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: ANIMATION_DURATION,
	},
	cardContent_expanded: {
		maxHeight: 'max-content',
	},
	cardContent_expanded__gradientSkrim: {
		opacity: 0,
		pointerEvents: 'none',
	},
	panel: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
	},
	chevronDownIconContainer: {
		width: '32px',
		height: '32px',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.full'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	hidden: {
		visibility: 'hidden',
		position: 'absolute',
		pointerEvents: 'none',
	},
	moreButtonBorder: {
		flexGrow: 1,
		alignSelf: 'stretch',
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		alignContent: 'center',
	},
	dropdownContainer: {
		width: 'fit-content',
	},
	tabWrapper: {
		overflow: 'hidden',
		minWidth: 0,
	},
	tabListWrapper: {
		overflow: 'hidden',
		minWidth: 0,
	},
	tabRow: {
		flexWrap: 'nowrap',
	},
});

const nativeStyles = nativeCssMap({
	cardContent: {
		interpolateSize: 'allow-keywords',
	},
	cardContent__gradientSkrim: {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- there is no token for gradient skrims. :(
		backgroundImage: 'linear-gradient(to top, var(--ds-surface) 0%, transparent 100%)',
	},
});

export type CardContentProps = {
	productKey: string;
	siteId: string;
};

export const CardContent = ({ productKey, siteId }: CardContentProps): React.JSX.Element => {
	di(useSolutionPlan);

	const [isExpanded, setIsExpanded] = useState(false);
	const { formatMessage } = useIntl();
	const { builds, currentBuildNumber } = useSolutionPlan();
	const [selectedTabIndex, setSelectedTabIndex] = useState(() =>
		getCurrentBuildIndex(builds, currentBuildNumber),
	);
	const { tabListRef, moreButtonRef, hasOverflow, hiddenTabIndices } =
		useOverflowTabs(selectedTabIndex);

	useEffect(() => {
		setSelectedTabIndex(getCurrentBuildIndex(builds, currentBuildNumber));
	}, [builds, currentBuildNumber]);

	if (builds.length === 0) {
		return (
			<Box xcss={styles.cardContent}>
				<SectionMessage title={formatMessage(messages.noBuildsTitle)} appearance="error">
					{formatMessage(messages.noBuildsDescription)}
				</SectionMessage>
			</Box>
		);
	}

	return (
		<Box
			xcss={cx(
				styles.cardContent,
				isExpanded && styles.cardContent_expanded,
				nativeStyles.cardContent,
			)}
		>
			<SmartCardProvider>
				{builds.length > 1 ? (
					<div ref={tabListRef}>
						<Tabs
							id="studio-trigger-solution-build-plan-card-tabs"
							onChange={(index) => setSelectedTabIndex(index)}
							selected={selectedTabIndex}
						>
							<Flex alignItems="center" xcss={styles.tabRow}>
								<Box xcss={styles.tabListWrapper}>
									<TabList>
										{builds.map((build, index) => {
											const isHidden = hiddenTabIndices.has(index);
											return (
												<Box key={build.number} xcss={isHidden ? styles.hidden : styles.tabWrapper}>
													<BuildTab
														build={build}
														label={build.name}
														index={index}
														stepNumber={index + 1}
													/>
												</Box>
											);
										})}
									</TabList>
								</Box>
								<Box xcss={styles.moreButtonBorder}>
									<Box
										ref={moreButtonRef}
										xcss={!hasOverflow ? styles.hidden : styles.dropdownContainer}
									>
										<DropdownMenu
											trigger={({ triggerRef, ...triggerProps }) => (
												<Button appearance="subtle" ref={triggerRef} {...triggerProps}>
													<Inline alignBlock="center" space="space.050">
														{formatMessage(messages.moreButtonLabel)}
														<Badge>{hiddenTabIndices.size}</Badge>
													</Inline>
												</Button>
											)}
										>
											<DropdownItemGroup>
												{Array.from(hiddenTabIndices.values()).map((tabIndex) => {
													const build = builds[tabIndex];
													return (
														<DropdownItem
															key={build.number}
															onClick={() => setSelectedTabIndex(tabIndex)}
														>
															{formatMessage(messages.tabLabelWithStep, {
																stepNumber: tabIndex + 1,
																label: build.name,
															})}
														</DropdownItem>
													);
												})}
											</DropdownItemGroup>
										</DropdownMenu>
									</Box>
								</Box>
							</Flex>
							{builds.map((build) => (
								<TabPanel key={build.number}>
									<Flex direction="column" gap="space.200" xcss={styles.panel}>
										<ContentRenderer
											content={build.overview}
											productKey={productKey}
											siteId={siteId}
										/>
									</Flex>
								</TabPanel>
							))}
						</Tabs>
					</div>
				) : (
					<ContentRenderer content={builds[0].overview} productKey={productKey} siteId={siteId} />
				)}
			</SmartCardProvider>
			<Flex
				xcss={cx(
					styles.cardContent__gradientSkrim,
					nativeStyles.cardContent__gradientSkrim,
					isExpanded && styles.cardContent_expanded__gradientSkrim,
				)}
				justifyContent="center"
				alignItems="center"
			>
				<Flex xcss={styles.chevronDownIconContainer} alignItems="center" justifyContent="center">
					<IconButton
						icon={ChevronDownIcon}
						label={formatMessage(messages.expandCardLabel)}
						shape="circle"
						onClick={() => setIsExpanded(!isExpanded)}
						testId="studio-trigger-solution-build-plan-card.chevron-down-icon-button"
						data-expanded={isExpanded}
					/>
				</Flex>
			</Flex>
		</Box>
	);
};
